import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotify, useDataProvider, Title } from 'react-admin';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Alert,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  getFeaturedVenueIds,
  saveFeaturedVenueIds,
  MAX_FEATURED_VENUES,
} from '../services/featuredVenuesService';

const FeaturedVenuesOrderPage = () => {
  const notify = useNotify();
  const navigate = useNavigate();
  const dataProvider = useDataProvider();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const ids = await getFeaturedVenueIds();
      const venues = await Promise.all(
        ids.map(async (id) => {
          try {
            const { data } = await dataProvider.getOne('venues', { id });
            return data;
          } catch {
            return { id, name: `(Silinmiş: ${id})`, missing: true };
          }
        }),
      );
      setRows(venues.filter(Boolean));
    } catch (err) {
      notify(err?.message || 'Öne çıkan mekanlar yüklenemedi', { type: 'warning' });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [dataProvider, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const move = (index, direction) => {
    setRows((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      const tmp = next[index];
      next[index] = next[target];
      next[target] = tmp;
      return next;
    });
  };

  const removeAt = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveFeaturedVenueIds(rows.map((r) => r.id));
      notify('Sıralama kaydedildi', { type: 'success' });
      await load();
    } catch (err) {
      notify(err?.message || 'Kayıt başarısız', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Title title="Öne Çıkan Mekan Sırası" />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('/venues')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5">Öne Çıkan Mekan Sırası</Typography>
          <Typography variant="body2" color="text.secondary">
            Soldan sağa web ana sayfa sırası (maks. {MAX_FEATURED_VENUES})
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading}
        >
          {saving ? 'Kaydediliyor…' : 'Kaydet'}
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : rows.length === 0 ? (
          <Alert severity="info">
            Henüz öne çıkan mekan yok. Mekanlar listesinden &quot;Öne Çıkar&quot;
            ile ekleyin.
          </Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={60}>Sıra</TableCell>
                <TableCell>Mekan</TableCell>
                <TableCell align="right" width={160}>
                  İşlem
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={row.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    {row.name}
                    {row.missing ? (
                      <Typography component="span" color="error" sx={{ ml: 1 }}>
                        (kayıt bulunamadı)
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      disabled={index === 0}
                      onClick={() => move(index, -1)}
                      aria-label="Yukarı"
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={index === rows.length - 1}
                      onClick={() => move(index, 1)}
                      aria-label="Aşağı"
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeAt(index)}
                      aria-label="Kaldır"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Container>
  );
};

export default FeaturedVenuesOrderPage;
